import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

export const CAMP_PHOTO_SLOTS = [
  { key: 'headshot', label: 'Headshot' },
  { key: 'full_body', label: 'Full Body' },
  { key: 'action', label: 'Action' },
];

// Camp photos are presigned S3 URLs on the regionless host
// (prozpects-media.s3.amazonaws.com), which the app's CSP img-src does NOT
// allow (it whitelists the regional s3.us-east-1 host). Route them through
// the same-origin image proxy so they render — same pattern as composite_url.
export const proxiedCampPhoto = (apiUrl, url) => `${apiUrl}/image-proxy?url=${encodeURIComponent(url)}`;

/**
 * Fetches an athlete's display camp photos (headshot/full_body/action) as
 * `[{ key, label, url }]`, keeping only captured slots. `userId` is the
 * ATHLETE's user id — never the logged-in user's when a guardian or staff
 * member is looking at someone else's profile.
 */
export function useCampPhotos(userId, apiUrl) {
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (!userId) { setPhotos([]); return undefined; }
    axios
      .get(`${apiUrl}/athletes/${userId}/camp-photos`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        setPhotos(
          CAMP_PHOTO_SLOTS.map((s) => ({ ...s, url: data[s.key] })).filter((s) => !!s.url)
        );
      })
      .catch(() => { if (!cancelled) setPhotos([]); });
    return () => { cancelled = true; };
  }, [apiUrl, userId]);

  return photos;
}

/**
 * Read-only strip of an athlete's camp photos (headshot/full_body/action),
 * shown inline with their other photos. Renders nothing when the athlete has
 * none. `onOpen(url)` lets a host reuse its own lightbox; without it, the
 * component opens its own full-screen overlay.
 */
export default function CampPhotos({ userId, onOpen }) {
  const { API_URL } = useAuth();
  const photos = useCampPhotos(userId, API_URL);
  const [internalLightbox, setInternalLightbox] = useState(null);

  if (photos.length === 0) return null;

  const proxied = (url) => proxiedCampPhoto(API_URL, url);
  const open = (url) => (onOpen ? onOpen(url) : setInternalLightbox(url));

  return (
    <div style={{ padding: '4px 24px 8px' }}>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {photos.map((p) => (
          <button
            type="button"
            key={p.key}
            onClick={() => open(proxied(p.url))}
            aria-label={`Open camp ${p.label} photo full size`}
            style={{
              width: '140px',
              aspectRatio: '4 / 5',
              background: '#f1f5f9',
              borderRadius: '8px',
              overflow: 'hidden',
              flexShrink: 0,
              border: 'none',
              padding: 0,
              cursor: 'zoom-in',
            }}
          >
            <img
              src={proxied(p.url)}
              alt={`Camp ${p.label}`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </button>
        ))}
      </div>

      {internalLightbox && (
        <div
          role="dialog"
          aria-label="Full size camp photo"
          onClick={() => setInternalLightbox(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            cursor: 'zoom-out',
          }}
        >
          <img
            src={internalLightbox}
            alt="Camp photo full size"
            style={{ maxWidth: '92vw', maxHeight: '92vh', objectFit: 'contain' }}
          />
        </div>
      )}
    </div>
  );
}
